import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ACCENTS,
  ThemeIds,
  accentById,
  accentFit,
  backgroundById,
  ratio,
} from '@porter/shared-theme';
import { resetButton, ring } from './constants';
import { accentContrastNotice } from './contrast';

interface Props {
  /** The background decides which accents paint as picked. */
  theme: ThemeIds;
  accentId: string | null;
  onSelect: (id: string) => void;
}

const FIT_HINT = {
  shifted: 'Shown as a different shade here so links stay readable.',
  replaced: "Can't be made readable here, so links use the page text color.",
} as const;

/** Swatches show the requested color. The page paints the contrast-fitted one. */
function AccentPicker({ theme, accentId, onSelect }: Props) {
  const background = backgroundById(theme.backgroundId);
  const notice = accentContrastNotice(theme);

  return (
    <>
      <Typography
        variant="body2"
        sx={{ margin: '0 0 12px', color: 'text.secondary' }}>
        Links, highlights and selected states.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(6, 1fr)', sm: 'repeat(8, 1fr)' },
          gap: '8px',
        }}>
        {ACCENTS.map(accent => {
          const fit = background ? accentFit(accent.hex, background) : 'kept';
          const swatch = (
            <Box
              key={accent.id}
              component="button"
              type="button"
              onClick={() => onSelect(accent.id)}
              aria-pressed={accent.id === accentId}
              aria-label={accent.name}
              sx={{
                ...resetButton,
                position: 'relative',
                display: 'block',
                width: '100%',
                '&::after': ring(accent.id === accentId, 3),
              }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 38,
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  background: accent.hex,
                }}>
                {fit !== 'kept' && (
                  <InfoOutlinedIcon
                    sx={{
                      position: 'absolute',
                      top: 3,
                      right: 3,
                      fontSize: 12,
                      opacity: 0.85,
                      color:
                        ratio('#FFFFFF', accent.hex) >=
                        ratio('#141414', accent.hex)
                          ? '#FFFFFF'
                          : '#141414',
                    }}
                  />
                )}
              </Box>
            </Box>
          );

          if (fit === 'kept') return swatch;
          return (
            <Tooltip
              key={accent.id}
              title={`${accent.name}: ${FIT_HINT[fit]}`}
              placement="top"
              arrow>
              {swatch}
            </Tooltip>
          );
        })}
      </Box>
      <Typography
        variant="caption"
        component="p"
        fontWeight={600}
        sx={{ marginTop: '10px', color: 'text.secondary' }}>
        {accentId ? accentById(accentId)?.name : 'None selected'}
      </Typography>
      {notice && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            marginTop: '8px',
            color: 'text.secondary',
          }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, marginTop: '1px' }} />
          <Typography variant="caption" component="p">
            {notice}
          </Typography>
        </Box>
      )}
    </>
  );
}

export default AccentPicker;
